// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';

import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import { useClaudeCheck, type ClaudeCheckAb, type ClaudeCheckKind, type ClaudeCheckRecent } from './useClaudeCheck';

/**
 * The verdict strengths offered side by side: the configured default, the smallest that takes a
 * confident classical bunker (0.70) under the 0.45 floor, and a harder one.
 */
const DELTAS = [0.25, 0.3, 0.4];

/**
 * The A/B for Claude's look at the unclear detections.
 *
 * Each detection in the unclear band is shown to Claude once, as a crop with its outline, and
 * the verdict is recorded beside it. This shows those verdicts against what people, OSM and GPS
 * say is really there, what they cost, and what letting them move confidence would change before
 * anybody switches that on (SatelliteAnalysis:ClaudeCheckApplies).
 */
export function ClaudeCheckPanel({ apiBaseUrl, token }: { apiBaseUrl: string; token: string | null | undefined }) {
  const { t } = useI18n();
  const [delta, setDelta] = useState<number | null>(null);
  const { ab, loading, error } = useClaudeCheck(apiBaseUrl, token, true, delta);

  return (
    <View style={styles.card} testID="claude-check-panel">
      <Text style={styles.title}>{t('claudeCheck.title')}</Text>
      <Text style={styles.legend}>{t('claudeCheck.legend')}</Text>

      {loading && !ab ? (
        <ActivityIndicator color={appColors.accent.primary} />
      ) : error === 'unavailable' ? (
        <Text style={styles.notice}>{t('claudeCheck.unavailable')}</Text>
      ) : error ? (
        <Text style={styles.notice}>{t('claudeCheck.failed')}</Text>
      ) : !ab ? null : (
        <Body ab={ab} loading={loading} onDelta={setDelta} />
      )}
    </View>
  );
}

function Body({ ab, loading, onDelta }: { ab: ClaudeCheckAb; loading: boolean; onDelta: (delta: number) => void }) {
  const { t } = useI18n();
  const { settings } = ab;

  return (
    <>
      <Text style={settings.applies ? styles.badge : styles.meta} testID="claude-check-in-force">
        {settings.applies
          ? t('claudeCheck.applies', { delta: settings.delta.toFixed(2) })
          : t('claudeCheck.notApplied')}
      </Text>
      <Text style={styles.meta} testID="claude-check-recording">
        {settings.enabled
          ? t('claudeCheck.recording', {
              model: settings.model,
              today: ab.sentToday,
              limit: settings.dailyLimit,
              input: ab.inputTokens,
              output: ab.outputTokens,
            })
          : t('claudeCheck.recordingOff')}
      </Text>
      <Text style={styles.meta}>
        {t('claudeCheck.band', {
          kinds: settings.kinds.map((kind) => t(`courseAnalysis.kind.${kind}`)).join(', '),
          min: settings.minConfidence.toFixed(2),
          max: settings.maxConfidence.toFixed(2),
          courses: ab.courses,
          floor: ab.servedFloor.toFixed(2),
        })}
      </Text>

      {ab.kinds.length === 0 ? (
        <Text style={styles.notice} testID="claude-check-empty">
          {t('claudeCheck.empty')}
        </Text>
      ) : (
        <>
          <Text style={styles.label}>{t('claudeCheck.delta')}</Text>
          <View style={styles.chips}>
            {DELTAS.map((value) => (
              <Choice
                key={value}
                label={value.toFixed(2)}
                selected={Math.abs(value - ab.delta) < 1e-9}
                onPress={() => onDelta(value)}
                testID={`claude-check-delta-${value}`}
              />
            ))}
          </View>
          {loading ? <Text style={styles.meta}>{t('claudeCheck.updating')}</Text> : null}

          {ab.kinds.map((kind) => (
            <KindRow key={kind.kind} kind={kind} delta={ab.delta} />
          ))}

          <Text style={styles.label}>{t('claudeCheck.recentHeading')}</Text>
          {ab.recent.map((verdict, index) => (
            <RecentRow key={`${verdict.courseId}-${index}`} verdict={verdict} />
          ))}
        </>
      )}
    </>
  );
}

function KindRow({ kind, delta }: { kind: ClaudeCheckKind; delta: number }) {
  const { t } = useI18n();

  return (
    <View style={styles.row} testID={`claude-check-kind-${kind.kind}`}>
      <Text style={styles.kind}>{t(`courseAnalysis.kind.${kind.kind}`)}</Text>
      <Text style={styles.meta}>
        {t('claudeCheck.verdicts', {
          yes: kind.yes,
          no: kind.no,
          unsure: kind.unsure,
          failed: kind.failed,
          noPicture: kind.noPicture,
        })}
      </Text>
      <Text style={styles.value} testID={`claude-check-truth-${kind.kind}`}>
        {t('claudeCheck.truthCounts', {
          yesReal: kind.yesReal,
          yesFalse: kind.yesFalse,
          noReal: kind.noReal,
          noFalse: kind.noFalse,
          unsureReal: kind.unsureReal,
          unsureFalse: kind.unsureFalse,
        })}
      </Text>
      <Text style={styles.value} testID={`claude-check-reach-${kind.kind}`}>
        {t('claudeCheck.reach', {
          delta: delta.toFixed(2),
          lifted: kind.lifted,
          liftedReal: kind.liftedReal,
          liftedFalse: kind.liftedFalse,
          dropped: kind.dropped,
          droppedReal: kind.droppedReal,
          droppedFalse: kind.droppedFalse,
        })}
      </Text>
    </View>
  );
}

function RecentRow({ verdict }: { verdict: ClaudeCheckRecent }) {
  const { t } = useI18n();

  return (
    <Text style={styles.meta} testID={`claude-check-recent-${verdict.courseId}-${verdict.holeNumber}`}>
      {t('claudeCheck.recent', {
        course: verdict.courseName ?? verdict.courseId,
        hole: verdict.holeNumber,
        kind: t(`courseAnalysis.kind.${verdict.kind}`),
        candidate: verdict.candidateConfidence.toFixed(2),
        verdict: t(`claudeCheck.verdict.${verdict.verdict}`),
        seenAs: verdict.seenAs ? t(`claudeCheck.seenAs.${verdict.seenAs}`) : '–',
        reason: verdict.reason,
        truth: t(`claudeCheck.truth.${verdict.truth}`),
      })}
    </Text>
  );
}

function Choice({
  label,
  selected,
  onPress,
  testID,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
  testID: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected }}
      onPress={onPress}
      style={[styles.chip, selected ? styles.chipSelected : null]}
      testID={testID}
    >
      <Text style={[styles.chipText, selected ? styles.chipTextSelected : null]}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: appColors.background.panel,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 14,
    gap: 10,
  },
  title: { color: appColors.text.primary, fontSize: 15, fontWeight: '700' },
  legend: { color: appColors.text.secondary, fontSize: 11 },
  notice: { color: appColors.text.secondary, fontSize: 13 },
  label: {
    color: appColors.text.secondary,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip: {
    height: 30,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipSelected: { borderColor: appColors.accent.primary },
  chipText: { color: appColors.text.secondary, fontSize: 11, fontWeight: '600' },
  chipTextSelected: { color: appColors.accent.primary },
  row: {
    gap: 2,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: appColors.surface.input,
  },
  kind: { color: appColors.text.primary, fontSize: 13, fontWeight: '700' },
  value: { color: appColors.text.primary, fontSize: 12 },
  meta: { color: appColors.text.secondary, fontSize: 11 },
  badge: {
    color: appColors.accent.primary,
    fontSize: 11,
    fontWeight: '700',
  },
});
